import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from 'configcat-react';
import { graphql, useStaticQuery } from 'gatsby';
import { map } from 'ramda';
import React, { FC, useContext, useEffect } from 'react';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import {
	IAuthenticatedMember,
	IInterest,
	IInterestData,
	IMemberInterestData,
} from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { combineMemberFragments, combineQueryFragments } from '@utils/graphql';

import FlowContext from '@providers/FlowContextProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import {
	interestsName,
	interests as interestsQuery,
	queryName as interestsQueryName,
} from '@graphql/fragments/interests';
import {
	memberInterestsName,
	memberInterests as memberInterestsQuery,
	queryName as memberInterestsQueryName,
} from '@graphql/fragments/memberInterests';
import UPDATE_MEMBER_INTERESTS from '@graphql/mutations/updateMemberInterests.mutation.gql';

import InterestCaptureStep, {
	IInterestCaptureStepPageContent,
} from './InterestCaptureStep.component';

interface IInterestCaptureStepPageProps extends IFlowStepProps {}

const InterestCaptureStepPage: FC<IInterestCaptureStepPageProps> = ({ path, stepKey }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "interest-capture-step" }) {
				page_header
				page_info
				member_interest_content {
					key
					value
				}
			}
		}
	`);

	const { handleNavNext } = useContext(FlowContext);

	const { value: isEnabled, loading: isFeatureFlagLoading } = useFeatureFlag(
		'interest-capture',
		false
	);

	useEffect(() => {
		if (isFeatureFlagLoading || isEnabled) {
			return;
		}

		handleNavNext(stepKey);
	}, [isEnabled, isFeatureFlagLoading]);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { data: interestData, loading: interestLoading } = useQuery<IInterestData>(
		combineQueryFragments([interestsName], [interestsQuery], interestsQueryName),
		{
			client: graphQLv2Client,
		}
	);
	const { data: memberInterestData, loading: memberInterestLoading } = useQuery<
		IAuthenticatedMember<IMemberInterestData>
	>(
		combineMemberFragments([memberInterestsName], [memberInterestsQuery], memberInterestsQueryName),
		{
			client: graphQLv2Client,
		}
	);

	const [updateMemberInterest] = useMutation(UPDATE_MEMBER_INTERESTS, { client: graphQLv2Client });

	const interests = map<IInterest, string>((interest) => {
		return interest.name;
	}, interestData?.getInterests?.nodes ?? []);

	const refinedContent: IInterestCaptureStepPageContent = keysToCamel(
		pageContent
	) as IInterestCaptureStepPageContent;

	return (
		<LoadingWrapper loading={memberInterestLoading || interestLoading}>
			<InterestCaptureStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				interests={interests}
				interestData={interestData?.getInterests?.nodes}
				memberInterests={memberInterestData?.authenticatedMember?.interests?.nodes}
				updateMemberInterest={updateMemberInterest}
				handleNavNext={handleNavNext}
			/>
		</LoadingWrapper>
	);
};

export default InterestCaptureStepPage;
